import type {
  BootstrapDeferredResponse,
  BootstrapProfileResponse,
  BootstrapResponse,
  ElClubHandoffRedirectResponse,
  ExchangeHandoffResponse,
  LoginResponse,
  LogoutResponse,
  ProfileResponse,
  RefreshTokenResponse,
  RegisterResponse,
  SelectCompanyLoginResponse,
  StartImpersonationResponse,
  ImpersonationStatus,
  UpdateProfileRequest,
  UpdateProfileResponse,
  UserRole,
} from '@crm/shared/frontend';
import { generateUUID } from '@crm/shared/uuidv7';
import {
  getUnauthorizedHandler,
  setUnauthorizedHandler,
  type UnauthorizedHandler,
} from './unauthorizedHandler';
import {
  getStoredAuthToken,
  getStoredRefreshTokenForRequest,
  setStoredAuthToken,
  setStoredRefreshToken,
  setStoredTokenExpiresAt,
} from './authStorage';
import {
  runCoordinatedSessionRefresh,
  type SessionRefreshUpdate,
} from './sessionRefreshCoordinator';
import { resolveBrowserApiBaseUrl } from '../utils/apiBaseUrl';

const API_BASE_URL = resolveBrowserApiBaseUrl();
const HTTP_STATUS_UNAUTHORIZED = 401;
const HANDOFF_EXCHANGE_KEY_SEPARATOR = '\u0000';
const START_IMPERSONATION_KEY_SEPARATOR = '\u0000';
const IDEMPOTENCY_KEY_HEADER_NAME = 'X-Idempotency-Key';
const FNV1A_32_OFFSET_BASIS = 0x811c9dc5;
const FNV1A_32_PRIME = 0x01000193;
const DJB2_32_OFFSET_BASIS = 5381;
const DJB2_32_SHIFT_BITS = 5;
const BYTE_HEX_RADIX = 16;
const PUBLIC_AUTH_ENDPOINTS = [
  '/api/auth/login',
  '/api/auth/select-company',
  '/api/auth/exchange-handoff',
  '/api/auth/elclub/exchange',
  '/api/auth/revoke-session',
  '/api/auth/refresh',
  '/api/auth/password/reset/request',
  '/api/auth/password/reset',
  '/api/auth/verify-email',
] as const;
const activeHandoffExchangeRequests = new Map<
  string,
  Promise<ExchangeHandoffResponse>
>();
const activeStartImpersonationRequests = new Map<
  string,
  Promise<StartImpersonationResponse>
>();

function applySessionRefreshUpdate(update: SessionRefreshUpdate): void {
  setStoredAuthToken(update.accessToken);
  if (update.refreshToken) {
    setStoredRefreshToken(update.refreshToken);
  }
  setStoredTokenExpiresAt(update.expiresAt.toString());
}

function fingerprintHandoffCredential(value: string): string {
  let fnvHash = FNV1A_32_OFFSET_BASIS;
  let djb2Hash = DJB2_32_OFFSET_BASIS;
  for (let index = 0; index < value.length; index += 1) {
    const charCode = value.charCodeAt(index);
    fnvHash ^= charCode;
    fnvHash = Math.imul(fnvHash, FNV1A_32_PRIME);
    djb2Hash = Math.imul(djb2Hash, 1 << DJB2_32_SHIFT_BITS) + djb2Hash;
    djb2Hash ^= charCode;
  }

  return [
    value.length,
    (fnvHash >>> 0).toString(BYTE_HEX_RADIX),
    (djb2Hash >>> 0).toString(BYTE_HEX_RADIX),
  ].join(':');
}

function buildHandoffExchangeKey(
  baseUrl: string,
  handoffToken: string,
  handoffProof: string
): string {
  return [
    baseUrl,
    fingerprintHandoffCredential(handoffToken),
    fingerprintHandoffCredential(handoffProof),
  ].join(HANDOFF_EXCHANGE_KEY_SEPARATOR);
}

interface ApiErrorPayload {
  message?: string;
  error?: string;
}

export class ApiRequestError extends Error {
  public readonly status: number;
  public readonly body?: unknown;

  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.body = body;
  }
}

export class AuthApiClient {
  private readonly baseURL: string;
  private refreshPromise: Promise<void> | null = null;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  public get onUnauthorized(): UnauthorizedHandler | null {
    const handler = getUnauthorizedHandler();
    return handler ?? null;
  }

  public set onUnauthorized(handler: UnauthorizedHandler | null) {
    if (handler === null) {
      setUnauthorizedHandler(null);
      return;
    }

    setUnauthorizedHandler(handler);
  }

  public async requestAuthenticated<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const authenticatedEndpoint = endpoint.trim();

    if (!authenticatedEndpoint.startsWith('/')) {
      throw new Error('Authenticated API endpoint must start with "/"');
    }

    return this.request<T>(authenticatedEndpoint, options);
  }

  public async requestPublic<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const publicEndpoint = endpoint.trim();

    if (!publicEndpoint.startsWith('/')) {
      throw new Error('Public API endpoint must start with "/"');
    }

    return this.request<T>(publicEndpoint, options, false, {
      includeAuthToken: false,
    });
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry = false,
    requestOptions: { includeAuthToken?: boolean } = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const token = getStoredAuthToken();
    const headers = new Headers(options.headers);
    const includeAuthToken = requestOptions.includeAuthToken !== false;

    if (!(options.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }

    if (includeAuthToken && token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers,
    });

    if (!response.ok) {
      if (includeAuthToken && response.status === HTTP_STATUS_UNAUTHORIZED) {
        const isPublicAuthEndpoint = PUBLIC_AUTH_ENDPOINTS.some((path) =>
          endpoint.startsWith(path)
        );

        if (!isRetry && !isPublicAuthEndpoint) {
          try {
            await this.refreshAccessToken();
            return this.request<T>(endpoint, options, true, requestOptions);
          } catch {
            // Fall through to unauthorized handling.
          }
        }

        if (!isPublicAuthEndpoint) {
          const handler = getUnauthorizedHandler();
          if (handler) {
            handler(token);
          }
        }
      }

      let errorBody: ApiErrorPayload | undefined;
      try {
        errorBody = (await response.json()) as ApiErrorPayload;
      } catch {
        errorBody = undefined;
      }

      throw new ApiRequestError(
        response.status,
        errorBody?.message ??
          errorBody?.error ??
          response.statusText ??
          `HTTP ${response.status}`,
        errorBody
      );
    }

    return response.json() as Promise<T>;
  }

  private async refreshAccessToken(): Promise<void> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const tokenAtRefreshStart = getStoredAuthToken();
    this.refreshPromise = runCoordinatedSessionRefresh({
      shouldRefresh: () => getStoredAuthToken() === tokenAtRefreshStart,
      refresh: () => this.refreshToken(getStoredRefreshTokenForRequest()),
      apply: applySessionRefreshUpdate,
    })
      .then(() => undefined)
      .finally(() => {
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  async login(email: string, password: string): Promise<LoginResponse> {
    return this.request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async loginPublicBuyer(
    email: string,
    password: string
  ): Promise<LoginResponse> {
    return this.request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, mode: 'public_buyer' }),
    });
  }

  async logout(): Promise<LogoutResponse> {
    return this.request<LogoutResponse>('/api/auth/logout', {
      method: 'POST',
    });
  }

  async revokeBrowserSession(): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>('/api/auth/revoke-session', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async selectCompanyLogin(
    selectionToken: string,
    companyId: string
  ): Promise<SelectCompanyLoginResponse> {
    return this.request<SelectCompanyLoginResponse>(
      '/api/auth/select-company',
      {
        method: 'POST',
        body: JSON.stringify({ selectionToken, companyId }),
      }
    );
  }

  async exchangeHandoff(
    handoffToken: string,
    handoffProof: string
  ): Promise<ExchangeHandoffResponse> {
    const exchangeKey = buildHandoffExchangeKey(
      this.baseURL,
      handoffToken,
      handoffProof
    );
    const activeExchange = activeHandoffExchangeRequests.get(exchangeKey);
    if (activeExchange) {
      return activeExchange;
    }

    const exchange = this.request<ExchangeHandoffResponse>(
      '/api/auth/exchange-handoff',
      {
        method: 'POST',
        body: JSON.stringify({ handoffToken, handoffProof }),
      }
    ).finally(() => {
      activeHandoffExchangeRequests.delete(exchangeKey);
    });
    activeHandoffExchangeRequests.set(exchangeKey, exchange);
    return exchange;
  }

  async createElClubHandoff(): Promise<ElClubHandoffRedirectResponse> {
    return this.request<ElClubHandoffRedirectResponse>(
      '/api/auth/elclub/handoff',
      {
        method: 'POST',
      }
    );
  }

  async refreshToken(refreshToken?: string): Promise<RefreshTokenResponse> {
    return this.request<RefreshTokenResponse>('/api/auth/refresh', {
      method: 'POST',
      body: JSON.stringify(refreshToken ? { refreshToken } : {}),
    });
  }

  async register(
    email: string,
    password: string,
    role: UserRole,
    employeeId?: string
  ): Promise<RegisterResponse> {
    return this.request<RegisterResponse>('/api/auth/register/employee', {
      method: 'POST',
      body: JSON.stringify({ email, password, role, employeeId }),
    });
  }

  async registerMarketplace(
    email: string,
    password: string
  ): Promise<RegisterResponse> {
    return this.request<RegisterResponse>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async verifyEmail(token: string): Promise<{ message: string }> {
    return this.request<{ message: string }>('/api/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async resendEmailVerification(email: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      '/api/auth/verify-email/resend',
      {
        method: 'POST',
        body: JSON.stringify({ email }),
      }
    );
  }

  async requestPasswordReset(email: string): Promise<{ message: string }> {
    return this.request<{ message: string }>(
      '/api/auth/password/reset/request',
      {
        method: 'POST',
        body: JSON.stringify({ email }),
      }
    );
  }

  async resetPassword(
    token: string,
    newPassword: string,
    confirmPassword = newPassword
  ): Promise<{ message: string }> {
    return this.request<{ message: string }>('/api/auth/password/reset', {
      method: 'POST',
      body: JSON.stringify({
        token,
        newPassword,
        confirmPassword,
      }),
    });
  }

  async getBootstrap(): Promise<BootstrapResponse> {
    return this.request<BootstrapResponse>('/api/bootstrap');
  }

  async getBootstrapProfile(): Promise<BootstrapProfileResponse> {
    return this.request<BootstrapProfileResponse>('/api/bootstrap/profile');
  }

  async getBootstrapDeferred(): Promise<BootstrapDeferredResponse> {
    return this.request<BootstrapDeferredResponse>('/api/bootstrap/deferred');
  }

  async getProfile(): Promise<ProfileResponse> {
    return this.request<ProfileResponse>('/api/profile');
  }

  async updateProfile(
    updates: UpdateProfileRequest
  ): Promise<UpdateProfileResponse> {
    return this.request<UpdateProfileResponse>('/api/profile', {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async startImpersonation(
    companyId: string,
    permissionProfileId?: string | null
  ): Promise<StartImpersonationResponse> {
    const hasSelectedPermissionProfileId = permissionProfileId !== undefined;
    const selectedPermissionProfileId = hasSelectedPermissionProfileId
      ? permissionProfileId
      : undefined;
    const permissionProfileKey = hasSelectedPermissionProfileId
      ? (selectedPermissionProfileId ?? 'admin')
      : 'reuse-existing-session';
    const startKey = [
      this.baseURL,
      companyId,
      permissionProfileKey,
    ].join(START_IMPERSONATION_KEY_SEPARATOR);
    const activeStart = activeStartImpersonationRequests.get(startKey);
    if (activeStart) {
      return activeStart;
    }

    const start = this.request<{ success: boolean }>(
      '/api/auth/impersonation/refresh-token-backup',
      {
        method: 'POST',
        body: JSON.stringify({}),
      }
    )
      .then(() =>
        this.request<StartImpersonationResponse>(
          `/api/admin/impersonate/${companyId}`,
          {
            method: 'POST',
            headers: {
              [IDEMPOTENCY_KEY_HEADER_NAME]: generateUUID(),
            },
            body: JSON.stringify(
              hasSelectedPermissionProfileId
                ? { permissionProfileId: selectedPermissionProfileId }
                : {}
            ),
          }
        )
      )
      .finally(() => {
        activeStartImpersonationRequests.delete(startKey);
      });
    activeStartImpersonationRequests.set(startKey, start);
    return start;
  }

  async endImpersonation(): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>(
      '/api/admin/impersonate',
      {
        method: 'DELETE',
      }
    );
  }

  async getCurrentImpersonationStatus(): Promise<ImpersonationStatus> {
    return this.request<ImpersonationStatus>('/api/admin/impersonate/current');
  }
}

export const authApiClient = new AuthApiClient();
